import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookMarked, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/errors";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        // Wipe the hash so a refresh doesn't retry
        window.history.replaceState({}, document.title, window.location.pathname);
        await refresh();
        toast.success(`Welcome, ${data.name || data.email.split("@")[0]}!`);
        nav("/", { replace: true });
      } catch (err) {
        const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Sign in failed";
        setError(msg);
      }
    })();
  }, [nav, refresh]);

  return (
    <div
      className="min-h-screen grid place-items-center bg-[#FFF5FA] dark:bg-[#1E1529] paper-grain"
      data-testid="auth-callback"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#F472B6] to-[#A78BFA] text-white grid place-items-center shadow-[0_4px_14px_rgba(244,114,182,0.45)] -rotate-6">
            <BookMarked className="w-6 h-6" />
          </div>
          <span className="font-serif-display text-2xl text-[#3B2350] dark:text-[#FBEFF6]">LitMap</span>
        </div>
        {error ? (
          <div className="max-w-sm">
            <p className="text-sm text-[#C9286F] dark:text-[#F9A8D4]">{error}</p>
            <button
              onClick={() => nav("/login", { replace: true })}
              className="mt-4 text-sm underline text-[#C9286F] dark:text-[#C4A1FF]"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-[#5E4B70] dark:text-[#BBA9CF]">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Signing you in…</span>
          </div>
        )}
      </div>
    </div>
  );
}
